import React from 'react';
import { Alert, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import usePermissions, { ManagerPermission } from '../../hooks/auth/usePermissions';
import useHistory from '../../hooks/navigation/useHistory';
import { ScreenEnum } from '../../hooks/navigation/Screens';
import { CompanyOperationalStatus } from '../../services/exports/Constants';
import { processingFeeBlocksRestaurant, setsStripeDestinationRates } from '../../services/helpers/StripeProcessingFees';

/**
 * A destination partner that has neither set its Stripe processing fee nor
 * confirmed a 0% margin can't take its Stripe restaurants live, and the live
 * ones take no online payment. Whoever sets its rates is told so on every page
 * but the fee page itself, with the number of live restaurants affected; the
 * restaurant's own users, who can't act on it, see why online payments are off.
 */
export default function StripeProcessingFeeBanner() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Layout:StripeProcessingFeeBanner' });
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const { company } = useSelector((state) => state.currentCompany);
  const { can } = usePermissions();
  const history = useHistory();
  const { pathname } = useLocation();

  const managesRates = setsStripeDestinationRates(serviceProvider) && can(ManagerPermission.WriteProcessingFeeRates);

  if (managesRates && serviceProvider?.stripe_processing_fee_setup_required === true) {
    if (pathname === ScreenEnum.PartnerProcessingFees) {
      return null;
    }

    const live = serviceProvider.stripe_processing_fee_blocked_live_restaurants ?? 0;

    return (
      <Alert
        banner
        type="error"
        data-testid="stripe-processing-fee-banner"
        title={
          <div className="flex items-center justify-between">
            <span>{live > 0 ? t('partner_live', { count: live }) : t('partner')}</span>
            <Button type="link" onClick={() => history.push(ScreenEnum.PartnerProcessingFees)}>
              {t('cta')}
            </Button>
          </div>
        }
      />
    );
  }

  if (
    !managesRates &&
    company?.operational_status === CompanyOperationalStatus.Live &&
    processingFeeBlocksRestaurant(company, company.service_provider)
  ) {
    return <Alert banner type="error" data-testid="stripe-processing-fee-banner" title={t('restaurant')} />;
  }

  return null;
}
